import { useMemo, useState } from "react";
import { useRoute, useLocation } from "wouter";
import { trpc } from "@/utils/trpc";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, Trash2, Rocket, AlertTriangle } from "lucide-react";
import { QA, useQaPerms } from "./qa-shared";

export default function QaTemplateEditor() {
  const [, params] = useRoute("/qa/templates/:versionId");
  const [, setLocation] = useLocation();
  const versionId = params?.versionId ? Number(params.versionId) : undefined;
  const perms = useQaPerms();

  const { data, isLoading, refetch } = trpc.qa.templates.getVersion.useQuery({ versionId: versionId! }, { enabled: !!versionId });
  const upsertDomain = trpc.qa.templates.upsertDomain.useMutation({ onSuccess: () => refetch() });
  const upsertSection = trpc.qa.templates.upsertSection.useMutation({ onSuccess: () => refetch() });
  const upsertQuestion = trpc.qa.templates.upsertQuestion.useMutation({ onSuccess: () => refetch() });
  const deleteQuestion = trpc.qa.templates.deleteQuestion.useMutation({ onSuccess: () => refetch() });
  const publish = trpc.qa.templates.publishVersion.useMutation({
    onSuccess: () => { toast.success("Version published"); setLocation("/qa/templates"); },
    onError: (e) => toast.error(e.message),
  });

  const [active, setActive] = useState(0);

  const totalWeight = useMemo(() => (data?.domains ?? []).reduce((a: number, d: any) => a + d.weightBps, 0), [data?.domains]);

  if (isLoading) return <div className="flex h-[50vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" style={{ color: QA.purple }} /></div>;
  if (!data) return <div className="p-6 text-sm text-gray-500">Version not found.</div>;

  const editable = data.version.status === "draft" && perms.templateManage;
  const domains = data.domains;
  const domain = domains[active];
  const domainSections = data.sections.filter((s: any) => s.domainId === domain?.id);

  return (
    <div className="mx-auto pb-10" style={{ marginTop: "26px", padding: "0 32px" }}>
      <button onClick={() => setLocation("/qa/templates")} className="mb-3 inline-flex items-center gap-1.5 text-[12px]" style={{ color: QA.text3 }}>
        <ArrowLeft className="h-3.5 w-3.5" /> Back to templates
      </button>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-[20px] font-bold" style={{ color: QA.text }}>Template Editor — v{data.version.versionNumber}</h1>
          <p className="text-[12px]" style={{ color: QA.text2 }}>
            Status: <span className="font-semibold capitalize" style={{ color: data.version.status === "published" ? QA.green : QA.amber }}>{data.version.status}</span>
            {!editable && data.version.status === "published" && " · Published versions are read-only. Create a new draft to edit."}
          </p>
        </div>
        {editable && (
          <button onClick={() => publish.mutate({ versionId: versionId! })} disabled={publish.isPending || totalWeight !== 10000}
            className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-50" style={{ background: QA.purple }}>
            <Rocket className="h-4 w-4" /> Publish Version
          </button>
        )}
      </div>

      {/* Weight integrity banner */}
      <div className="mb-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-[12px]" style={{ borderColor: totalWeight === 10000 ? QA.green : QA.amber, background: totalWeight === 10000 ? QA.greenPale : QA.amberPale, color: totalWeight === 10000 ? QA.green : QA.amber }}>
        {totalWeight !== 10000 && <AlertTriangle className="h-3.5 w-3.5" />}
        Total domain weight: {(totalWeight / 100).toFixed(0)}% {totalWeight !== 10000 && "(must equal 100% to publish)"}
      </div>

      <div className="flex gap-4">
        {/* Domain tabs */}
        <aside className="w-[200px] shrink-0 space-y-1">
          {domains.map((d: any, i: number) => (
            <button key={d.id} onClick={() => setActive(i)} className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-[12px]"
              style={{ background: i === active ? QA.purpleXPale : "#fff", border: `1px solid ${QA.border}`, color: i === active ? QA.purple : QA.text }}>
              <span className="truncate font-semibold">{d.name}</span>
              <span className="text-[11px]" style={{ color: QA.text3 }}>{(d.weightBps / 100).toFixed(0)}%</span>
            </button>
          ))}
          {editable && (
            <button onClick={() => {
              const name = prompt("New domain name"); if (!name) return;
              upsertDomain.mutate({ versionId: versionId!, code: `D${domains.length + 1}`, name, weightBps: 0, sortOrder: domains.length });
            }} className="flex w-full items-center justify-center gap-1 rounded-lg border border-dashed px-3 py-2 text-[12px] font-semibold" style={{ borderColor: QA.borderMid, color: QA.purple }}>
              <Plus className="h-3.5 w-3.5" /> Add Domain
            </button>
          )}
        </aside>

        {/* Domain content — keyed by domain.id so all fields remount on switch */}
        <main className="min-w-0 flex-1">
          {domain && (
            <div key={domain.id} className="rounded-xl border bg-white p-4" style={{ borderColor: QA.border }}>
              <div className="mb-3 flex flex-wrap items-center gap-3">
                <input defaultValue={domain.name} disabled={!editable} onBlur={(e) => editable && e.target.value !== domain.name && upsertDomain.mutate({ id: domain.id, versionId: versionId!, code: domain.code, name: e.target.value, weightBps: domain.weightBps, sortOrder: domain.sortOrder })}
                  className="flex-1 rounded-md border px-2.5 py-1.5 text-[14px] font-bold" style={{ borderColor: QA.border, color: QA.text }} />
                <label className="flex items-center gap-1.5 text-[12px]" style={{ color: QA.text2 }}>
                  Weight %
                  <input type="number" defaultValue={domain.weightBps / 100} disabled={!editable} onBlur={(e) => editable && upsertDomain.mutate({ id: domain.id, versionId: versionId!, code: domain.code, name: domain.name, weightBps: Math.round(Number(e.target.value) * 100), sortOrder: domain.sortOrder })}
                    className="w-20 rounded-md border px-2 py-1 text-[12px]" style={{ borderColor: QA.border }} />
                </label>
              </div>

              {domainSections.map((section: any) => (
                <div key={section.id} className="mb-4">
                  <div className="mb-2 flex items-center gap-2 border-b pb-1.5 text-[12px] font-bold uppercase tracking-wide" style={{ color: QA.text3, borderColor: QA.border }}>
                    {section.name}
                  </div>
                  {data.questions.filter((q: any) => q.sectionId === section.id).map((q: any) => (
                    <div key={q.id} className="mb-2.5 rounded-lg border p-2.5" style={{ borderColor: QA.border, background: QA.surface }}>
                      <div className="flex items-start gap-2">
                        <span className="mt-1.5 shrink-0 text-[11px] font-bold" style={{ color: QA.purple }}>{q.questionNumber}</span>
                        <textarea defaultValue={q.text} disabled={!editable} rows={1} onBlur={(e) => editable && e.target.value !== q.text && upsertQuestion.mutate({ id: q.id, versionId: versionId!, domainId: domain.id, sectionId: section.id, questionNumber: q.questionNumber, text: e.target.value, allowNotApplicable: q.allowNotApplicable, sortOrder: q.sortOrder })}
                          className="flex-1 resize-y rounded-md border bg-white px-2.5 py-1.5 text-[12px]" style={{ borderColor: QA.border, color: QA.text }} />
                        {editable && <button onClick={() => deleteQuestion.mutate({ id: q.id, versionId: versionId! })} className="mt-1"><Trash2 className="h-3.5 w-3.5" style={{ color: QA.red }} /></button>}
                      </div>
                      {/* Answer-options preview (how this question is answered on the questionnaire) */}
                      <div className="mt-2 flex flex-wrap items-center gap-3 pl-7 text-[11px]">
                        <span className="font-semibold uppercase tracking-wide" style={{ color: QA.text3 }}>Answer options:</span>
                        <span className="flex items-center gap-1" style={{ color: QA.text2 }}>
                          Availability <Chip text="Yes" c={QA.green} /> <Chip text="No" c={QA.red} />
                        </span>
                        <span className="flex items-center gap-1" style={{ color: QA.text2 }}>
                          Being in Practice <Chip text="Yes" c={QA.green} /> <Chip text="No" c={QA.red} /> {q.allowNotApplicable && <Chip text="N/A" c={QA.purple} />}
                        </span>
                        <span className="flex items-center gap-1" style={{ color: QA.text2 }}>
                          <Chip text="Remarks" c={QA.gray} /> <Chip text="Evidence" c={QA.gray} />
                        </span>
                        <label className="ml-auto flex items-center gap-1.5" style={{ color: QA.text2 }}>
                          <input type="checkbox" disabled={!editable} checked={!!q.allowNotApplicable}
                            onChange={(e) => editable && upsertQuestion.mutate({ id: q.id, versionId: versionId!, domainId: domain.id, sectionId: section.id, questionNumber: q.questionNumber, text: q.text, allowNotApplicable: e.target.checked, sortOrder: q.sortOrder })} />
                          Allow N/A
                        </label>
                      </div>
                    </div>
                  ))}
                  {editable && (
                    <button onClick={() => {
                      const count = data.questions.filter((q: any) => q.sectionId === section.id).length;
                      upsertQuestion.mutate({ versionId: versionId!, domainId: domain.id, sectionId: section.id, questionNumber: `${section.code.replace(/^S/, "")}.${count + 1}`, text: "New question", allowNotApplicable: true, sortOrder: count + 1 });
                    }} className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: QA.purple }}>
                      <Plus className="h-3 w-3" /> Add question
                    </button>
                  )}
                </div>
              ))}

              {editable && (
                <button onClick={() => {
                  const name = prompt("New section name"); if (!name) return;
                  upsertSection.mutate({ versionId: versionId!, domainId: domain.id, code: `S${domain.code}.${domainSections.length + 1}`, name, sortOrder: domainSections.length });
                }} className="inline-flex items-center gap-1 rounded-lg border border-dashed px-3 py-1.5 text-[12px] font-semibold" style={{ borderColor: QA.borderMid, color: QA.purple }}>
                  <Plus className="h-3.5 w-3.5" /> Add Section
                </button>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function Chip({ text, c }: { text: string; c: string }) {
  return <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: `${c}1a`, color: c }}>{text}</span>;
}
